import {
  type ItineraryVersionDiff,
  type ItineraryVersionDiffChange,
  type ItineraryVersionSummary,
} from '@utazzo/shared';
import { UzAlert, UzBadge, UzSpinner } from '@utazzo/ui';
import { ArrowRight, CheckCircle2, Clock, GitCompareArrows } from 'lucide-react';
import React, { useMemo } from 'react';

import {
  useItineraryDetail,
  useItineraryVersions,
  useItineraryVersionDiff,
} from '../use-itineraries';

import { DeltaBadge, fmtMoney } from './price-visibility';

import { useCurrentUser } from '@/features/auth/use-auth';

const SECTION_LABEL: Record<ItineraryVersionDiffChange['section'], string> = {
  basics: 'Trip basics',
  travellers: 'Travellers & rooms',
  route: 'Route & flights',
  addons: 'Add-ons',
  content: 'Content',
  money: 'Pricing',
  plan: 'Day plan',
};

const SECTION_ORDER: ItineraryVersionDiffChange['section'][] = [
  'basics',
  'travellers',
  'route',
  'addons',
  'content',
  'money',
  'plan',
];

/** Same sequence as PricingStep "Quote total" sidebar. */
const MONEY_FIELD_ORDER = [
  'netCostPaise',
  'flightCostPaise',
  'hotelCostPaise',
  'transferCostPaise',
  'activityCostPaise',
  'addonCostPaise',
  'netMarkupPaise',
  'agentMarkupPaise',
  'discountPaise',
  'additionalDiscountPaise',
  'repriceTargetPaise',
  'gstPaise',
  'tcsPaise',
  'grandTotalPaise',
] as const;

const moneyFieldRank = (field: string): number => {
  const i = (MONEY_FIELD_ORDER as readonly string[]).indexOf(field);
  return i === -1 ? MONEY_FIELD_ORDER.length : i;
};

/**
 * Changes tab — field-level diff of this itinerary vs its parent version.
 * Shown for every version that has a `parentItineraryId` (v2+).
 *
 * Money rows: ADMIN sees absolute previous → current amounts. Agents / sales
 * leads only see the +/- movement (same contract as Days / Preview cards).
 */
/** IST-local date-time, e.g. "22 Jul 2026, 11:32". */
const fmtDateTime = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * Publish info for the version currently being viewed only — its Quote No.,
 * when it was published + by whom, and (admin) its grand total. The field-level
 * diff below already carries the price movement vs the parent, so this stays a
 * single compact card rather than the full multi-version history (which the
 * version dropdown already lists).
 */
const PublishHistory = ({
  leadId,
  currentItineraryId,
  canSeeAmounts,
}: {
  leadId: string;
  currentItineraryId: string;
  canSeeAmounts: boolean;
}): React.JSX.Element | null => {
  const query = useItineraryVersions(leadId);
  const current = useMemo(
    () =>
      (query.data ?? []).find(
        (v: ItineraryVersionSummary) => v.itineraryId === currentItineraryId && v.publishedAt,
      ) ?? null,
    [query.data, currentItineraryId],
  );
  if (!current) return null;
  return (
    <section className="rounded-lg border border-violet-300 bg-violet-50/60 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Clock className="text-primary h-4 w-4" aria-hidden />
          <UzBadge tone="secondary" pill>
            Quote No. {current.quoteNo ?? current.version}
          </UzBadge>
          {current.isCurrentPublished && (
            <UzBadge tone="success" pill>
              Current
            </UzBadge>
          )}
          {current.acceptedAt && (
            <UzBadge tone="success" pill>
              Accepted
            </UzBadge>
          )}
        </div>
        {canSeeAmounts && (
          <span className="text-sm font-semibold text-zinc-900 tabular-nums">
            {fmtMoney(current.grandTotalPaise) ?? '—'}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Published {fmtDateTime(current.publishedAt)}
        {current.publishedByName ? ` by ${current.publishedByName}` : ''}
        {current.quoteId ? ` · ${current.quoteId}` : ''}
        {current.acceptedAt ? ` · accepted ${fmtDateTime(current.acceptedAt)}` : ''}
      </p>
    </section>
  );
};

export const VersionDiffTab = ({ itineraryId }: { itineraryId: string }): React.JSX.Element => {
  const query = useItineraryVersionDiff(itineraryId);
  const leadId = useItineraryDetail(itineraryId).data?.leadId ?? null;
  const role = useCurrentUser()?.role;
  const canSeeAmounts = role === 'ADMIN';
  // GST / TCS diffs are ADMIN-only (not sales lead / agent / ops).
  const canSeeTax = role === 'ADMIN';
  const isHiddenTaxField = (field: string): boolean => field === 'gstPaise' || field === 'tcsPaise';

  const renderDiff = (): React.JSX.Element => {
    if (query.isLoading) {
      return (
        <div className="flex h-48 items-center justify-center gap-2 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Comparing with previous version…
        </div>
      );
    }

    if (query.isError) {
      return (
        <UzAlert tone="info">
          No earlier version to compare with. This is the first published version of the itinerary,
          so there are no field-level changes yet.
        </UzAlert>
      );
    }

    const diff = query.data as ItineraryVersionDiff;
    const visibleChanges = canSeeTax
      ? diff.changes
      : diff.changes.filter((c) => !isHiddenTaxField(c.field));
    const changed = visibleChanges.filter((c) => c.changed);
    // Non-admins never see absolute money in the unchanged accordion either.
    const unchanged = visibleChanges.filter((c) => {
      if (c.changed) return false;
      if (!canSeeAmounts && c.section === 'money') return false;
      return true;
    });

    return (
      <>
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <GitCompareArrows className="text-primary h-5 w-5" aria-hidden />
              <h2 className="text-base font-semibold text-zinc-900">
                Changes from v{diff.parentRevisionNumber}
              </h2>
            </div>
            <p className="mt-1 text-sm text-zinc-500">
              Comparing this version (v{diff.revisionNumber}) with the previous version (v
              {diff.parentRevisionNumber}).
              {!canSeeAmounts && (
                <>
                  {' '}
                  Price rows show only the increase or decrease from the previous version —
                  including flights, hotels, activities, and transfers.
                </>
              )}
            </p>
          </div>
          {diff.identical ? (
            <UzBadge tone="success" className="gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              Identical to previous
            </UzBadge>
          ) : (
            <UzBadge tone="warning">
              {changed.length} change{changed.length === 1 ? '' : 's'}
            </UzBadge>
          )}
        </header>

        {diff.identical ? (
          <UzAlert tone="info">
            No field-level differences from v{diff.parentRevisionNumber}. This usually means the
            version was created with &ldquo;Copy current version&rdquo; and hasn&apos;t been edited
            yet.
          </UzAlert>
        ) : (
          <DiffSections changes={changed} emptyLabel={null} canSeeAmounts={canSeeAmounts} />
        )}

        {unchanged.length > 0 && (
          <details className="rounded-lg border border-zinc-200 bg-zinc-50/60">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-zinc-700">
              Unchanged fields ({unchanged.length})
            </summary>
            <div className="border-t border-zinc-200 px-4 py-3">
              <DiffSections
                changes={unchanged}
                emptyLabel="—"
                muted
                canSeeAmounts={canSeeAmounts}
              />
            </div>
          </details>
        )}
      </>
    );
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-16">
      {leadId && (
        <PublishHistory
          leadId={leadId}
          currentItineraryId={itineraryId}
          canSeeAmounts={canSeeAmounts}
        />
      )}
      {renderDiff()}
    </div>
  );
};

const DiffSections = ({
  changes,
  emptyLabel,
  muted = false,
  canSeeAmounts,
}: {
  changes: ItineraryVersionDiffChange[];
  emptyLabel: string | null;
  muted?: boolean;
  canSeeAmounts: boolean;
}): React.JSX.Element => {
  const bySection = useMemo(() => {
    const map = new Map<ItineraryVersionDiffChange['section'], ItineraryVersionDiffChange[]>();
    for (const c of changes) {
      const list = map.get(c.section) ?? [];
      list.push(c);
      map.set(c.section, list);
    }
    return SECTION_ORDER.filter((s) => map.has(s)).map((s) => {
      const rows = map.get(s)!;
      if (s === 'money') {
        rows.sort((a, b) => moneyFieldRank(a.field) - moneyFieldRank(b.field));
      }
      return { section: s, rows };
    });
  }, [changes]);

  return (
    <div className="space-y-5">
      {bySection.map(({ section, rows }) => (
        <section key={section}>
          <h3
            className={
              'mb-2 text-xs font-semibold tracking-wider uppercase ' +
              (muted ? 'text-zinc-400' : 'text-zinc-500')
            }
          >
            {SECTION_LABEL[section]}
          </h3>
          <ul className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 bg-white">
            {rows.map((row) => (
              <DiffRow
                key={row.field}
                row={row}
                emptyLabel={emptyLabel}
                muted={muted}
                canSeeAmounts={canSeeAmounts}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

const DiffRow = ({
  row,
  emptyLabel,
  muted,
  canSeeAmounts,
}: {
  row: ItineraryVersionDiffChange;
  emptyLabel: string | null;
  muted: boolean;
  canSeeAmounts: boolean;
}): React.JSX.Element => {
  const isMoney = row.section === 'money';
  const delta =
    row.deltaPaise !== null && row.deltaPaise !== undefined
      ? Number(row.deltaPaise)
      : isMoney &&
          row.previousPaise !== null &&
          row.previousPaise !== undefined &&
          row.currentPaise !== null &&
          row.currentPaise !== undefined
        ? Number(row.currentPaise) - Number(row.previousPaise)
        : null;
  const showDeltaOnly = isMoney && !canSeeAmounts;

  return (
    <li
      className={
        'grid gap-2 px-4 py-3 sm:grid-cols-[160px_1fr] ' +
        (row.changed && !muted ? 'bg-amber-50/40' : '')
      }
    >
      <div className="text-sm font-medium text-zinc-800">{row.label}</div>
      {showDeltaOnly ? (
        <div className="flex min-w-0 items-center">
          {row.changed && delta !== null && Number.isFinite(delta) && delta !== 0 ? (
            <DeltaBadge
              paise={delta}
              currency="INR"
              title={
                delta > 0 ? 'Higher than the previous version' : 'Lower than the previous version'
              }
            />
          ) : (
            <span className="text-sm text-zinc-400">No price change</span>
          )}
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3">
          <ValueCell
            label="Previous"
            value={row.previous ?? emptyLabel}
            strike={row.changed && !muted}
          />
          {row.changed && !muted && (
            <ArrowRight
              className="mt-1 hidden h-4 w-4 shrink-0 text-zinc-400 sm:block"
              aria-hidden
            />
          )}
          <ValueCell
            label="Current"
            value={row.current ?? emptyLabel}
            emphasize={row.changed && !muted}
          />
          {isMoney &&
            canSeeAmounts &&
            row.changed &&
            !muted &&
            delta !== null &&
            Number.isFinite(delta) &&
            delta !== 0 && (
              <div className="sm:pt-4">
                <DeltaBadge
                  paise={delta}
                  currency="INR"
                  title={
                    delta > 0
                      ? 'Higher than the previous version'
                      : 'Lower than the previous version'
                  }
                />
              </div>
            )}
        </div>
      )}
    </li>
  );
};

const ValueCell = ({
  label,
  value,
  strike,
  emphasize,
}: {
  label: string;
  value: string | null;
  strike?: boolean;
  emphasize?: boolean;
}): React.JSX.Element => (
  <div className="min-w-0 flex-1">
    <div className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">{label}</div>
    <div
      className={
        'mt-0.5 text-sm break-words ' +
        (value == null
          ? 'text-zinc-300 italic'
          : emphasize
            ? 'font-medium text-zinc-900'
            : strike
              ? 'text-zinc-500 line-through'
              : 'text-zinc-700')
      }
    >
      {value ?? 'Not set'}
    </div>
  </div>
);
